<script>
import { mapState, mapActions } from 'pinia';
import { useFeedStore } from '@store/useFeedStore.js';
import { useHeaderStore } from '@store/useHeaderStore.js';
import { useSidebarStore } from '@store/useSidebarStore.js';

const SIDEBAR_LINK_CLICKED_EVENT = 'crowdblue:sidebar-link-clicked';
const SIDEBAR_INVITE_CLICKED_EVENT = 'crowdblue:sidebar-invite-clicked';
import { STICKY_SIDEBAR_TOP } from '@resources/js/constants';
import { SIDEBAR_MAIN_ITEMS, SIDEBAR_MORE_ITEMS, SIDEBAR_CATEGORY_ROUTES, SIDEBAR_VALID_CATEGORIES, getRunToWinLinkFromState } from './sidebarMenuConfig.js';

export default {
  name: 'CpSidebarMenu',
  mixins: [require('@resources/js/mixins/breakpoints.js').default],
  components: {
    CpDropdownMenu: require('@resources/js/social-feed/DropdownMenu').default,
    CpInviteMembersDialog: require('@resources/js/dialogs/invite-members').default,
    CpButtonLink: require('@resources/js/button-link').default,
    CpButton: require('@resources/js/button').default,
  },
  directives: {
    margin: require('@resources/js/directives/margins.js').margin,
    marginTop: require('@resources/js/directives/margins.js').marginTop,
    marginBottom: require('@resources/js/directives/margins.js').marginBottom,
    marginRight: require('@resources/js/directives/margins.js').marginRight,
  },
  props: {
    navigationMode: {
      type: String,
      default: 'spa',
      validator: value => ['spa', 'fullPage'].includes(value),
    },
    runToWinLink: { type: String, default: '' },
  },
  data() {
    return {
      activeDataCategory: '',
      mainItems: SIDEBAR_MAIN_ITEMS,
      moreItems: SIDEBAR_MORE_ITEMS,
    };
  },
  created() {
    if (this.isSpaMode && SIDEBAR_VALID_CATEGORIES.includes(this.sidebarCategory)) {
      this.activeDataCategory = this.sidebarCategory;
    }
  },
  methods: {
    ...mapActions(useSidebarStore, ['toggleCollapsed', 'toggleMoreOptions']),
    handleLinkClick(categoryClicked) {
      const path = categoryClicked === 'home' ? '/feed' : `/${categoryClicked}`;

      if (this.isSpaMode) {
        this.activeDataCategory = categoryClicked;
        window.history.pushState({ category: categoryClicked }, '', path);
        useFeedStore().updateSidebarCategoty(categoryClicked);
        window.dispatchEvent(
          new CustomEvent(SIDEBAR_LINK_CLICKED_EVENT, {
            detail: { category: categoryClicked },
          }),
        );
        this.$emit('sidebarLinkClicked', categoryClicked);
        return;
      }

      window.location.href = path;
    },
    handleInviteButtonClicked() {
      window.dispatchEvent(new CustomEvent(SIDEBAR_INVITE_CLICKED_EVENT));
      this.$emit('inviteButtonClicked');
    },
    handleMoreOptions() {
      this.toggleMoreOptions();
    },
    isActiveCategory(category) {
      const currentPath = window.location.pathname;
      const expectedPath = SIDEBAR_CATEGORY_ROUTES[category];

      if (!expectedPath) {
        return false;
      }

      if (category === 'home') {
        return currentPath === '/feed';
      }

      return currentPath === expectedPath;
    },
    isItemActive(category) {
      return this.isSpaMode ? this.activeDataCategory === category : this.isActiveCategory(category);
    },
    getItemLabel(item) {
      if (item.truncateOnSmallDesktop && this.isSmallDesktop) {
        return 'Represent...';
      }

      return item.label;
    },
  },
  computed: {
    ...mapState(useFeedStore, ['sidebarCategory']),
    ...mapState(useHeaderStore, ['sitewideBannerInfo']),
    ...mapState(useSidebarStore, ['isCollapsed', 'isMoreOptionsOpen']),
    showCollapsedLayout() {
      return this.isCollapsed && !this.isMobile;
    },
    isSpaMode() {
      return this.navigationMode === 'spa';
    },
    calculatedStickySidebarTop() {
      return this.sitewideBannerInfo.visible ? STICKY_SIDEBAR_TOP + this.sitewideBannerInfo.height : STICKY_SIDEBAR_TOP;
    },
    resolvedRunToWinLink() {
      return this.runToWinLink || getRunToWinLinkFromState();
    },
    resolvedInviteUrl() {
      return this.inviteUrl || `${this.appUrl}/feed`;
    },
    resolvedInviteTitle() {
      if (this.inviteTitle) {
        return this.inviteTitle;
      }

      const user = this.$root.authUser;

      return user ? `${user.fullname} wants you to join CrowdBlue!` : 'You have been invited to join CrowdBlue!';
    },
    resolvedInviteDescription() {
      return this.inviteDescription || this.resolvedInviteTitle;
    },
    resolvedInvitingUserId() {
      if (this.invitingUserId) {
        return this.invitingUserId;
      }

      return this.$root.authUser ? String(this.$root.authUser.id) : '0';
    },
    showSiteSearch() {
      return window.location.pathname !== '/user/login';
    },
  },
  watch: {
    sidebarCategory(newVal) {
      if (this.isSpaMode) {
        this.activeDataCategory = newVal;
      }
    },
  },
};
</script>

<template>
  <div
    :class="{
      'sticky-sidebar-menu-container': true,
      'sidebar-collapsed': showCollapsedLayout,
    }"
  >
      <CpButton v-if="!isMobile" noborder class="sidebar-toggle-btn" :class="{ 'sidebar-link-collapsed': showCollapsedLayout }" @click="toggleCollapsed">
        <i class="fal icon" :class="isCollapsed ? 'fa-indent' : 'fa-outdent'"></i>
      </CpButton>
      <div v-if="showSiteSearch" class="sidebar-search-container" :class="{ 'sidebar-search-container-collapsed': showCollapsedLayout }">
        <cp-site-search />
    </div>
    <nav
      :class="{
        mobile: isMobile,
        tablet: isTablet,
        collapsed: showCollapsedLayout,
      }"
    >
      <CpButton
        :primary="isItemActive(item.id)"
        :noborder="!isItemActive(item.id)"
        v-for="item in mainItems"
        :key="item.id"
        :class="['sidebar-link', { 'sidebar-link-collapsed': showCollapsedLayout, active: isItemActive(item.id) }]"
        @click="handleLinkClick(item.id)"
      >
        <div class="button-with-content">
          <i class="fal icon" :class="item.icon"></i>
          <span class="subhead sidebar-menu-title">{{ getItemLabel(item) }}</span>
        </div>
      </CpButton>

      <CpButton noborder class="sidebar-link neutral-2-color" @click="handleMoreOptions()" :class="{ 'sidebar-link-collapsed': showCollapsedLayout }">
        <div class="button-with-content">
          <span class="subhead sidebar-menu-title" v-margin-right="showCollapsedLayout ? 0 : 1">More</span>
          <i class="fal icon fa-chevron-down" :class="{ 'is-open-more': isMoreOptionsOpen }"></i>
        </div>
      </CpButton>

      <div v-show="isMoreOptionsOpen" class="more-options-container">
        <CpButton
          :primary="isItemActive(item.id)"
          :noborder="!isItemActive(item.id)"
          v-for="item in moreItems"
          :key="item.id"
          :class="['sidebar-link', { 'sidebar-link-collapsed': showCollapsedLayout, active: isItemActive(item.id), mobile: $root.isMobile }]"
          @click="handleLinkClick(item.id)"
        >
          <div class="button-with-content">
            <i class="fal icon" :class="item.icon"></i>
            <span class="subhead sidebar-menu-title">{{ getItemLabel(item) }}</span>
          </div>
        </CpButton>
      </div>
    </nav>

    <div class="sidebar-extras" :class="{ 'sidebar-extras--collapsed': showCollapsedLayout }">
      <CpDropdownMenu :run-to-win-link="resolvedRunToWinLink" v-margin-top="0.3" v-margin-bottom="0.8" />
      <CpInviteMembersDialog
        :url="resolvedInviteUrl"
        :title="resolvedInviteTitle"
        :description="resolvedInviteDescription"
        quote
        twitter-user="crowdblue"
        hashtags="crowdBlue,politics,political crowdfunding"
        :inviting-user-id="resolvedInvitingUserId"
        v-on:inviteButtonClicked="handleInviteButtonClicked"
      />
    </div>
  </div>
</template>

<style scoped>
nav {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--white);
}
.sidebar-search-container {
  margin-top: 12px;
  margin-bottom: 12px;
  height: 38px;
  width: 100%;
}
.sidebar-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: var(--primary);
}
.sidebar-toggle-btn:hover {
  background-color: var(--primary-1);
  color: var(--primary-3);
}
.sidebar-toggle-btn:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.sidebar-collapsed {
  width: fit-content;
  max-width: fit-content;
}
.sticky-sidebar-menu-container {
  width: 100%;
  background-color: var(--white);
  padding: 12px 10px;
  border-radius: 0;
  box-sizing: border-box;
  min-height: calc(100vh - var(--site-header-sidebar-top, 102px));
  height: 100%;
  max-height: calc(100vh - var(--site-header-sidebar-top, 102px));
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  transition: width 0.3s ease, padding 0.3s ease;
}
.sticky-sidebar-menu-container::-webkit-scrollbar {
  width: 5px;
}
.sticky-sidebar-menu-container::-webkit-scrollbar-thumb {
  background-color: var(--primary);
  border-radius: 3px;
}
.sidebar-link-collapsed {
  width: fit-content;
}
.sidebar-link {
  color: var(--primary-3);
  justify-content: flex-start;
}
.sidebar-link :deep(.button__content) {
  justify-content: flex-start;
}
.sidebar-link:hover,
.sidebar-link.active,
nav.mobile .sidebar-link.active {
  background-color: var(--primary-1);
}
.button-with-content {
  display: flex;
  align-items: center;
  gap: 10px;
}
nav.tablet .social-sidebar-menu-item {
  padding: 5px 5px 5px 10px;
  margin-bottom: 7px;
}
nav.tablet span.sidebar-menu-title {
  font-size: 0.8em;
}

nav.tablet .social-sidebar-menu-item svg {
  max-width: 25px;
}
span.sidebar-menu-title {
  cursor: inherit;
}
nav.mobile .sidebar-link {
  justify-content: flex-start;
}
.more-options-container--mobile-flat {
  padding-left: 0;
}
.icon {
  font-size: 18px;
  transition: transform 0.3s ease, opacity 0.3s ease;
  transform-origin: center;
  text-align: center;
}
.social-sidebar-menu-item .icon.is-open-more {
  transform: rotate(180deg);
}
.more-options-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 18px;
}
nav.collapsed .sidebar-menu-title {
  display: none;
}
nav.collapsed .more-options-container {
  padding-left: 0;
}
nav.collapsed div.social-sidebar-menu-item {
  justify-content: flex-start;
  padding: 9px;
}
nav.collapsed a.sidebar-link {
  margin: 6px 0;
}
nav.collapsed .icon {
  flex: 0 0 auto;
  text-align: left;
  width: 1em;
}
.sidebar-extras {
  margin-top: 12px;
  width: 100%;
}
.sidebar-extras :deep(.invite-members-button-container) {
  display: inline-flex;
}
.sidebar-extras :deep(.invite-members-button) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}
.sidebar-extras--collapsed {
  width: auto;
}
.sidebar-search-container-collapsed,
.sidebar-extras--collapsed :deep(.dropbtn),
.sidebar-extras--collapsed :deep(.invite-members-button) {
  width: 48px;
  height: 38px;
}
.sidebar-extras--collapsed :deep(.invite-members-button-container) {
  width: 48px;
}
.sidebar-extras--collapsed :deep(.dropbtn .subhead),
.sidebar-extras--collapsed :deep(.invite-member-button-text) {
  display: none;
}
@media screen and (max-width: 768px) {
  .sticky-sidebar-menu-container {
    padding: 0px;
    border: none;
    min-height: unset;
  }
  nav.mobile .sidebar-menu-title {
    display: inline;
  }
  .more-options-container .icon {
    font-size: 18px;
  }
}
@media screen and (max-width: 450px) {
  .more-options-container .icon {
    font-size: 16px;
  }
}
</style>
